import { fn } from '@ember/helper';
/* eslint-disable ember/no-runloop, ember/no-side-effects */
import { action } from '@ember/object';
import { begin, end } from '@ember/runloop';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismLoadingBar, PrismTooltip } from '@mylighthouse/prism-ember';
import { PRISM_SPACING_400 } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { and, not } from 'ember-truth-helpers';
import * as Highcharts from 'highcharts';
import GeneralChartsLegendsLegendItem from 'lumen/components/general/charts/legends/legend-item';
import GeneralChartsOtaChart from 'lumen/components/general/charts/ota-chart';
import GeneralEmptyStateOtbBanner from 'lumen/components/general/empty-state-otb-banner';
import HotelOverviewDashboardTooltipsTooltipDailyPatterns from 'lumen/components/hotel/overview-dashboard/tooltips/tooltip-daily-patterns';
import isOdTileVisible from 'lumen/helpers/is-od-tile-visible';
import { getWeekendPlotBands } from 'lumen/utils/charts/chart-utils';
import { addNoDataZone, addZone, NO_DATA_ZONE } from 'lumen/utils/charts/zones';
import { DATE_AGGREGATION } from 'lumen/utils/constants/general-constants';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import {
  OD_API_DATA_KEYS,
  OD_REF_DAYS_AGO_VALUES,
  OD_SELECTED_TILES_IDS,
} from 'lumen/utils/constants/overview-dashboard-constants';
import {
  SHORT_TERM_RENTAL_OTB,
  SHORT_TERM_RENTAL_RATE,
} from 'lumen/utils/constants/short-term-rental-constants';
import {
  getDateAggregation,
  tickPositioner,
  xAxisLabelFormatter,
} from 'lumen/utils/overview-dashboard/daily-patterns-utils';

// To stack columns on top of each other (with overlap), we need to create a new series type
Highcharts.seriesType('stackedColumn', 'column', {});

interface HotelOverviewDashboardTilesTopInsightsDailyPatternsSignature {
  Args: {
    isLoading: unknown;
    numberOfCompetitors: unknown;
    onUpdateChartReference: unknown;
    otbData: unknown;
    priceOpportunityData: unknown;
    pricePositionData: unknown;
    rangeChanged: unknown;
    rentalData: unknown;
    selectedRefDaysAgo: unknown;
    toggleOrSetSelectedDay: unknown;
  };
}

export default class HotelOverviewDashboardTilesTopInsightsDailyPatterns extends Component<HotelOverviewDashboardTilesTopInsightsDailyPatternsSignature> {
  // eslint-disable-next-line ember/no-unused-services
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare odAccess: Services['od-access'];
  @service declare odSelections: Services['od-selections'];

  // We disable the opportunity legend item in case there is not opportunities in the selected period
  @tracked areOpportunitiesInSelectedRange = false;
  @tracked isMarketOtbSeriesVisible = false;
  @tracked isMarketPickupSeriesVisible = false;
  @tracked isMyOtbSeriesVisible = true;
  @tracked isMyPickupSeriesVisible = true;
  @tracked isMyPriceOpportunitySeriesVisible = true;
  @tracked isMyPricePositionSeriesVisible = true;
  @tracked isRentalOtbSeriesVisible = false;
  @tracked isRentalPickupSeriesVisible = false;
  @tracked tooltipData = null;

  otbAxisTickInterval = 0.25;

  // Adding some extra space on top of the price position axis so that the price position values of 1 aren't cutt off
  pricePositionPaddingTop = 0.5;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get activeDrilldownDayPlotLines() {
    // @ts-expect-error: Incorrect type
    if (!this.odSelections.isDayDrilldownActive) {
      return [];
    }

    let activeDrilldownMoment = this.hotelAndMarketService.toLocalTime(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      this.odSelections.activeDrilldownDayAsString,
    );

    // In case of week aggregation, plotLine drawn at value equals to the xValue of the week in which the drilled down day belongs
    // @ts-expect-error: Incorrect type
    if (!this.isDailyAggregation && this.dataSourceToLoopFromDates.fromDates) {
      const startDate = this.hotelAndMarketService.toLocalTime(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.dataSourceToLoopFromDates.fromDates[0].fromDate,
      );

      const nbWeeksFromStart = activeDrilldownMoment.diff(startDate, 'week');

      activeDrilldownMoment = startDate.add(nbWeeksFromStart, 'week');
    }

    const activeDrilldownDayXvalue = this.hotelAndMarketService.toLocalUnixTime(
      activeDrilldownMoment.unix(),
    );

    return [
      {
        color: 'var(--prism-color-chart-accent-primary-figure-default)',
        value: activeDrilldownDayXvalue,
        zIndex: 1, // In order to position plotLines above plotBands else theya re hidden beneath the weekendPlotBand
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canShowOtbData() {
    return (
      (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_otb) ||
        this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.market_otb)) &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.otbData.fromDates.length !== 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canShowPriceOpportunityData() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.demand_level) &&
      // @ts-expect-error: Incorrect type
      this.odAccess.isMarketInsightFull &&
      this.args.priceOpportunityData &&
      // @ts-expect-error: Incorrect type
      this.args.priceOpportunityData.length !== 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canShowPricePositionData() {
    return (
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.pricing_difference) &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.pricePositionData.fromDates.length !== 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canShowShortTermRentalData() {
    return (
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.short_term_rental) &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.rentalData.fromDates.length !== 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get cardTileTitle() {
    if (this.isDailyAggregation) {
      return this.intl.t('overview_dashboard.kpi.header.daily_patterns');
    }

    return this.intl.t('overview_dashboard.kpi.header.weekly_patterns');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartData() {
    const chartSeries = [];

    const myOtbSeriesConfig = {
      id: 'my-otb',
      type: 'column',
      color: 'var(--prism-color-general-neutral-500)',
      visible: this.isMyOtbSeriesVisible,
      zIndex: 15,
      data: [],
    };

    const myPickupSeriesConfig = {
      id: 'my-otb-pickup',
      type: 'stackedColumn',
      color: 'var(--prism-color-chart-categorical-19)',
      visible: this.isMyPickupSeriesVisible,
      zIndex: 20,
      data: [],
      xAxis: 1,
    };

    const marketOtbSeriesConfig = {
      id: 'market-otb',
      type: 'area',
      color: 'var(--prism-color-chart-categorical-10)',
      fillOpacity: 0.15,
      borderWidth: 1,
      visible: this.isMarketOtbSeriesVisible,
      marker: { enabled: false },
      zIndex: 5,
      data: [],
    };

    const marketPickupSeriesConfig = {
      name: 'market-otb-pickup',
      type: 'area',
      color: 'var(--prism-color-chart-categorical-17)',
      fillOpacity: 0.15,
      borderWidth: 1,
      visible: this.isMarketPickupSeriesVisible,
      marker: { enabled: false },
      zIndex: 5,
      data: [],
    };

    const myPricePositionSeriesConfig = {
      id: 'price-position',
      type: 'line',
      color: 'var(--prism-color-chart-accent-primary-figure-default)',
      fillOpacity: 0.15,
      borderWidth: 1,
      yAxis:
        (this.canShowOtbData || this.canShowShortTermRentalData) &&
        this.canShowPricePositionData
          ? 1
          : 0,
      visible: this.isMyPricePositionSeriesVisible,
      marker: { enabled: false },
      zIndex: 20,
      zones: [],
      zoneAxis: 'x',
      connectNulls: true,
      data: [],
    };

    // Line type rather than scatter because Highcharts generates the tooltip of a scatter on the top/bottom rather than the desired left/right
    const myPriceOpportunitySeriesConfig = {
      id: 'price-opportunity',
      zIndex: 20,
      type: 'line',
      lineWidth: 0, // Hide the line as we only want to see the price opportunity markers
      states: {
        hover: {
          lineWidthPlus: 0, // Hide the line on hover also
        },
      },
      marker: {
        lineColor: 'var(--prism-color-chart-accent-primary-figure-emphasis)',
        lineWidth: 3,
        radius: 4,
        fillColor: 'var(--prism-color-elevation-surface)',
      },
      yAxis:
        (this.canShowOtbData || this.canShowShortTermRentalData) &&
        this.canShowPriceOpportunityData &&
        this.canShowPricePositionData
          ? 1
          : 0,
      visible: this.isMyPriceOpportunitySeriesVisible,
      data: [],
    };

    const rentalOtbSeriesConfig = {
      id: 'short-term-rental-otb',
      data: [],
      type: 'column',
      color: SHORT_TERM_RENTAL_OTB,
      zIndex: 1,
      visible: this.isRentalOtbSeriesVisible,
    };

    const rentalPickupSeriesConfig = {
      type: 'stackedColumn',
      xAxis: 1,
      id: 'short-term-rental-pickup',
      data: [],
      color: SHORT_TERM_RENTAL_RATE,
      zIndex: 1,
      visible: this.isRentalPickupSeriesVisible,
    };

    const emptyOtbSeriesConfig = {
      type: 'column',
    };

    const emptyPickupSeriesConfig = {
      type: 'stackedColumn',
      xAxis: 1,
    };

    // @ts-expect-error: Incorrect type
    let lastNonNullDataValue = null;

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    this.dataSourceToLoopFromDates.fromDates?.forEach((datapoint, index) => {
      const xValue = this.hotelAndMarketService.toLocalUnixTime(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        this.hotelAndMarketService.toLocalTime(datapoint.fromDate).unix(),
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { fromDate } = datapoint;

      // Get both chart datapoints, with safety checks (as not every subscription will have access to these)
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const otbDatapointObject = this.args.otbData?.fromDates[index];
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      const rentalOtbDataPointObject = this.args.rentalData?.fromDates[index];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const pricePositionDatapointObject =
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        this.args.pricePositionData?.fromDates[index];

      /* The endpoint for the opportunities always returns the data organised by days
       * To be able to show it correctly for weekly and day view we need to split the set of priceOpportunityDatapointObject in two cases
       */
      let priceOpportunityDatapointObject = { hasOpportunity: false };

      if (this.isDailyAggregation) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        priceOpportunityDatapointObject = this.args.priceOpportunityData?.find(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          (data) => data.date === fromDate,
        );
      } else {
        let hasOpportunity = false;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const startWeek = dayjs(fromDate);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        const endWeek = dayjs(fromDate).add(6, 'day');

        for (let day = startWeek; day <= endWeek; day = day.add(1, 'day')) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          hasOpportunity =
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            this.args.priceOpportunityData?.find(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (data) => data.date === day.format('YYYY-MM-DD'),
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            )?.hasOpportunity || hasOpportunity;
        }

        priceOpportunityDatapointObject.hasOpportunity = hasOpportunity;
      }

      // Boolean that indicates if a datapoint should be highlighted or not (basically means that the dashboard drilldown is pointing to it)
      let isFromDateHighlightedOnDayDrilldown = false;

      // Separate check depending on the aggregation
      if (this.isDailyAggregation) {
        // @ts-expect-error: Incorrect type
        if (fromDate === this.odSelections.activeDrilldownDayAsString) {
          isFromDateHighlightedOnDayDrilldown = true;
        }
        // Support weekly aggregation below
      } else if (
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        dayjs(this.odSelections.activeDrilldownDayAsString).isBetween(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          dayjs(fromDate),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          dayjs(fromDate).add(6, 'day'),
          'day',
          // @ts-expect-error: Incorrect type
          [],
        )
      ) {
        isFromDateHighlightedOnDayDrilldown = true;
      }

      // Preserve values of all the data points regardless if the corresponding series is toggled/untoggled
      const tooltipData = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        myOtbValue: otbDatapointObject?.getDataValueFromKey(
          this.odSelections,
          OD_API_DATA_KEYS.my_otb_percentage,
        ),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        myOtbRoomnightsValue: otbDatapointObject?.getDataValueFromKey(
          this.odSelections,
          OD_API_DATA_KEYS.my_otb_roomnights,
        ),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        myOtbPickupPercentageValue: otbDatapointObject?.getDataValueFromKey(
          this.odSelections,
          OD_API_DATA_KEYS.my_otb_percentage_pickup,
          // @ts-expect-error: Incorrect type
          this.args.selectedRefDaysAgo.id,
        ),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        myOtbPickupRoomnightsValue: otbDatapointObject?.getDataValueFromKey(
          this.odSelections,
          OD_API_DATA_KEYS.my_otb_roomnights_pickup,
          // @ts-expect-error: Incorrect type
          this.args.selectedRefDaysAgo.id,
        ),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        marketOtbValue: otbDatapointObject?.getDataValueFromKey(
          this.odSelections,
          OD_API_DATA_KEYS.market_otb_percentage,
        ),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        marketPickupValue: otbDatapointObject?.getDataValueFromKey(
          this.odSelections,
          OD_API_DATA_KEYS.market_otb_percentage_pickup,
          // @ts-expect-error: Incorrect type
          this.args.selectedRefDaysAgo.id,
        ),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        rentalOtbValue: rentalOtbDataPointObject?.getDataValueFromKey(
          this.odSelections,
          OD_API_DATA_KEYS.rental_otb,
        ),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        rentalPickupValue: rentalOtbDataPointObject?.getDataValueFromKey(
          this.odSelections,
          OD_API_DATA_KEYS.rental_otb_diff,
          // @ts-expect-error: Incorrect type
          this.args.selectedRefDaysAgo.id,
        ),
        hasOpportunity: priceOpportunityDatapointObject?.hasOpportunity,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        opportunity: priceOpportunityDatapointObject?.opportunity,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        medianCompsetPrice:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          pricePositionDatapointObject?.kpis.median_compset_price,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        myPricePosition: pricePositionDatapointObject?.kpis.position,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        price: pricePositionDatapointObject?.kpis.price,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        message: pricePositionDatapointObject?.kpis.message,
      };

      // Reduce the column opacity of the bars that are not selected during the day drilldown being active
      let className = '';
      if (
        // @ts-expect-error: Incorrect type
        this.odSelections.isDayDrilldownActive &&
        !isFromDateHighlightedOnDayDrilldown
      ) {
        className = 'daily-patterns-highcharts-opacity';
      }

      // @ts-expect-error: Incorrect type
      myOtbSeriesConfig.data.push({
        x: xValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y: tooltipData.myOtbValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        fromDate,
        className,
        tooltipData,
      });

      // @ts-expect-error: Incorrect type
      myPickupSeriesConfig.data.push({
        x: xValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y: tooltipData.myOtbPickupPercentageValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        fromDate,
        className,
        tooltipData,
      });

      // @ts-expect-error: Incorrect type
      marketOtbSeriesConfig.data.push({
        x: xValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y: tooltipData.marketOtbValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        fromDate,
        tooltipData,
      });

      // @ts-expect-error: Incorrect type
      marketPickupSeriesConfig.data.push({
        x: xValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y: tooltipData.marketPickupValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        fromDate,
        tooltipData,
      });

      // @ts-expect-error: Incorrect type
      rentalOtbSeriesConfig.data.push({
        x: xValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y: tooltipData.rentalOtbValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        fromDate,
        tooltipData,
      });

      // @ts-expect-error: Incorrect type
      rentalPickupSeriesConfig.data.push({
        x: xValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        y: tooltipData.rentalPickupValue,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        fromDate,
        tooltipData,
      });

      if (this.canShowPricePositionData) {
        let marker = { enabled: false };

        // enable marker in following cases:
        // 1. day drilldown
        // 2. single day selected
        if (
          // @ts-expect-error: Incorrect type
          (this.odSelections.isDayDrilldownActive &&
            isFromDateHighlightedOnDayDrilldown) ||
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          this.args.pricePositionData.fromDates.length === 1
        ) {
          marker = { enabled: true };
        }

        // @ts-expect-error: Incorrect type
        myPricePositionSeriesConfig.data.push({
          x: xValue,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          y: tooltipData.myPricePosition,
          marker,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          fromDate,
          tooltipData,
        });

        // Displaying null values of price position series as dotted line. Or in case there is a message as well (LOSX / Soldout ...)
        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          pricePositionDatapointObject.kpis.position &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          !pricePositionDatapointObject.kpis.message
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          lastNonNullDataValue = pricePositionDatapointObject.kpis.position;

          const lastZone =
            myPricePositionSeriesConfig.zones[
              myPricePositionSeriesConfig.zones.length - 1
            ];
          // @ts-expect-error: Incorrect type
          if (lastZone && lastZone.dashStyle === NO_DATA_ZONE.dashStyle) {
            // If the last zone was a nodata zone, add a new zone
            addZone(
              myPricePositionSeriesConfig.zones,
              xValue + this.unixDifferenceBetweenIndexes,
            );
          }
        } else {
          // Case 1: Add no data zone for null values that are surrounded by non null values
          addNoDataZone(
            myPricePositionSeriesConfig.zones,
            { x: xValue - this.unixDifferenceBetweenIndexes },
            { x: xValue + this.unixDifferenceBetweenIndexes },
            xValue,
          ); // Zone goes from the previous point to the next point

          // Case 2: Add no data zone for null values that are not surrounded by non null values
          // For there to be dotted line for null values there should be a non null value around this null value zone
          // Therefore, no dotted line would be displayed in the case where the data either starts with all null values or end with all null values
          // However, in rates graph, we have the following behavior:
          // Case 2.1: All null values in the beginning, we don't display any dotted line
          // Case 2.2: All null values at the end, we display dotted line
          // In order to achieve the same behavior as described in Case 2.2, we set the end most null value with the last non null value occuring and disable the marker hover for this end most point.

          // The data ending with null value
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (index === this.args.pricePositionData.fromDates.length - 1) {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            myPricePositionSeriesConfig.data[index].y = lastNonNullDataValue;
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            myPricePositionSeriesConfig.data[index].marker = {
              states: { hover: { enabled: false } },
            };
          }
        }
      }

      if (this.canShowPriceOpportunityData && tooltipData.hasOpportunity) {
        // @ts-expect-error: Incorrect type
        myPriceOpportunitySeriesConfig.data.push({
          x: xValue,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          y: tooltipData.myPricePosition,
          marker: { enabled: true },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          fromDate,
          tooltipData,
        });
      }
    });

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_otb)) {
      chartSeries.push(myOtbSeriesConfig);
      chartSeries.push(myPickupSeriesConfig);
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.market_otb)) {
      chartSeries.push(marketOtbSeriesConfig);
      chartSeries.push(marketPickupSeriesConfig);
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.short_term_rental)) {
      chartSeries.push(rentalPickupSeriesConfig);
      chartSeries.push(rentalOtbSeriesConfig);

      if (this.isRentalPickupSeriesVisible || this.isRentalOtbSeriesVisible) {
        if (!this.isRentalPickupSeriesVisible) {
          chartSeries.push(emptyPickupSeriesConfig);
        }
        if (!this.isRentalOtbSeriesVisible) {
          chartSeries.push(emptyOtbSeriesConfig);
        }
      }
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.pricing_difference)) {
      chartSeries.push(myPricePositionSeriesConfig);
    }

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.demand_level)) {
      chartSeries.push(myPriceOpportunitySeriesConfig);
      this.areOpportunitiesInSelectedRange =
        myPriceOpportunitySeriesConfig.data.length !== 0;
    }

    return chartSeries;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get chartOptions() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    return {
      chart: {
        type: 'column',
        height: 350,
        spacingLeft: PRISM_SPACING_400,
        spacingBottom: PRISM_SPACING_400,
        spacingTop: PRISM_SPACING_400, // Needs to be set or the top axis labels are cut off
        spacingRight: PRISM_SPACING_400, // Needs to be set or the secondary Y axis labels are cut off a bit
        zoomType: null,
        alignTicks: false, // to ensure that both y axis don't align their ticks
      },
      xAxis: [
        {
          type: 'datetime',
          crosshair: true,
          plotLines: that.activeDrilldownDayPlotLines,
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          tickPositioner() {
            return tickPositioner(
              // @ts-expect-error: Incorrect type
              this.dataMin,
              // @ts-expect-error: Incorrect type
              this.dataMax,
              that.isDailyAggregation,
            );
          },
          labels: {
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-return
              return xAxisLabelFormatter(
                // @ts-expect-error: Incorrect type
                this.value,
                that.isDailyAggregation,
                that.intl,
              );
            },
          },
          plotBands: this.weekendPlotBands,
        },
        {
          visible: false,
        },
      ],
      yAxis: this.yAxis,
      tooltip: {
        hideDelay: 0.2,
        crosshairs: true,
        padding: 0,
        borderWidth: 0,
        shadow: false,
        distance: 20,
        shared: true,
        outside: true,
        // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
        formatter() {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          const day = this.points ? this.points[0].point : this.point;
          // Necessary to get correct data for tooltips, needs to be in separate runloop
          begin();
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          that.tooltipData = {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            fromDateString: day.fromDate,
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/restrict-plus-operands
            numberOfCompetitors: that.numberOfCompetitors + 1, // we show My price position out of numberOfCompetitors + 1
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ...day.tooltipData,
          };
          end();

          // @ts-expect-error: Incorrect type
          return document.getElementById('dummy-tooltip-for-daily-patterns')
            .innerHTML;
        },
        useHTML: true,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        column: {
          shadow: false,
          borderWidth: 1,
          pointWidth: null, // Enables dynamic column width based on amount of bars
          maxPointWidth: 40,
          minPointLength: 2,
          states: {
            hover: {
              brightness: 0,
              borderColor:
                'var(--prism-color-interactive-border-neutral-hover)',
            },
          },
        },
        series: {
          // cursor: that.isDailyAggregation ? 'pointer' : null,
          cursor: 'pointer',
          animation: false,
          point: {
            events: {
              // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
              click() {
                // Activate the drilldown when the user clicks on a bar that is not already highlighted
                // If the user clicks on the highlighted bar again, deactivate the day drilldown
                if (that.isDailyAggregation) {
                  // @ts-expect-error: Incorrect type
                  that.args.toggleOrSetSelectedDay(this.fromDate);
                } else {
                  // Temp behaviour for series click in case of weekly aggregation
                  // We basically update the overview from/toDate parameters to the clicked week
                  // This will update the whole dashboard to show that range. In case users want to go back, they can use the back button
                  // @ts-expect-error: Incorrect type
                  that.args.rangeChanged({
                    // @ts-expect-error: Incorrect type
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    start: dayjs(this.fromDate),
                    // @ts-expect-error: Incorrect type
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    end: dayjs(this.fromDate).add(6, 'day'),
                  });
                }
              },
            },
          },
        },
      },
    };
  }

  // We always use one data source to loop over and to create the chartData
  // The available data sources will depend on the subscription (e.g. no rates -> no price position data / no otb -> no otb data)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dataSourceToLoopFromDates() {
    let dataSourceToLoopFromDates = [];

    if (this.canShowPricePositionData) {
      // @ts-expect-error: Incorrect type
      dataSourceToLoopFromDates = this.args.pricePositionData;
    } else if (this.canShowOtbData) {
      // @ts-expect-error: Incorrect type
      dataSourceToLoopFromDates = this.args.otbData;
    } else if (this.canShowShortTermRentalData) {
      // @ts-expect-error: Incorrect type
      dataSourceToLoopFromDates = this.args.rentalData;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return dataSourceToLoopFromDates;
  }

  // true when the data aggregation is daily, false when the data aggregation is weekly
  // data aggregation is weekly when the selected date range is greater than 30 days in between
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isDailyAggregation() {
    const dateAggregation = getDateAggregation(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.selectedRange.start,
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.selectedRange.end,
    );

    return dateAggregation === DATE_AGGREGATION.DAY;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfCompetitors() {
    if (this.canShowPricePositionData) {
      return this.args.numberOfCompetitors;
    }

    return null;
  }

  /*
    Problem: The extra 5% padding to the OTB axis is added as an extra tick. It is possible to hide the label of this tick but not the respective grid line
    Therefore, we draw custom plotLines for all the OTB axis ticks except the max + 5% one
  */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get otbAxisPlotLines() {
    const otbYaxisMinMaxValues = this.calculateOtbYaxisMinMaxValue();
    const otbAxisTickPositions = this.otbYAxisTickPositioner(
      this.otbAxisTickInterval,
      otbYaxisMinMaxValues,
    );

    return otbAxisTickPositions.map((value) => ({
      color: 'var(--prism-color-border-neutral-default)',
      value,
    }));
  }

  // This chart uses X axis of datetime category. Normally we often use the category type here (so we can access previous/next datapoints via index)
  // This helper gives the value we can add/subtract from the current xValue to access the previous/next datapoint (unix timestamps)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get unixDifferenceBetweenIndexes() {
    return this.isDailyAggregation
      ? 1000 * 60 * 60 * 24
      : 1000 * 60 * 60 * 24 * 7;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get weekendPlotBands() {
    // Only show weekend plot bands in case of daily aggregation
    // @ts-expect-error: Incorrect type
    if (this.dataSourceToLoopFromDates.fromDates && this.isDailyAggregation) {
      // @ts-expect-error: Incorrect type
      const { fromDates } = this.dataSourceToLoopFromDates;
      const startDate = this.hotelAndMarketService.toLocalTime(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        fromDates[0].fromDate,
      );
      const endDate = this.hotelAndMarketService.toLocalTime(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        fromDates[fromDates.length - 1].fromDate,
      );

      return getWeekendPlotBands(
        this.hotelAndMarketService,
        startDate,
        endDate,
      );
    }

    return null;
  }

  /*
    yAxis part of this component is pretty dependant on the subscription. There are 4 cases:
    - OTB & price position data available: OTB will be primary Y axis & price position secondary Y axis
    - only OTB data available: OTB will be primary Y axis
    - only price position data available: price position will be primary Y axis
    - STR & price position available (no OTB): STR OTB will be primary Y axis & price position will be secondary Y axis

    In scenarios where we're dealing with a primary & secondary Y axis, we also have some extra logic to align the ticks
  */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get yAxis() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    const axisArray = [];

    if (
      this.canShowOtbData ||
      this.canShowShortTermRentalData ||
      this.canShowPricePositionData
    ) {
      if (this.canShowOtbData || this.canShowShortTermRentalData) {
        const primaryYaxis = {
          title: {
            text: that.intl.t('rates.OTB'),
          },
          gridLineColor: 'transparent', // We set plotlines already
          plotLines: this.otbAxisPlotLines,
          /**
           * A custom tick positioner is needed here instead of just specifying min/max for the primary axis because we want this OTB axis to rescale based on whether on the pickup series are visible or not
           * The min/max are calculated once when the graph is rendered. There is no way to update these on the legend toggle once the graph has been rendered.
           * Whereas The tickPositioner runs every time when the series corresponding to the primary yAxis ( myOtb / marketOtb / myPickup / marketPickup) are toggled on/off.
           * */
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          tickPositioner() {
            const otbYaxisMinMaxValues = that.calculateOtbYaxisMinMaxValue();

            const tickPositions = that.otbYAxisTickPositioner(
              that.otbAxisTickInterval,
              otbYaxisMinMaxValues,
            );

            const otbYaxisMaxValue =
              that.calculateOtbYaxisIntervalRange(otbYaxisMinMaxValues.max) /
              100;

            // calculate the otb axis value corresponding to the 0.5 units of price position value
            // we add this to the max of otb axis in order to create an equal padding on top of the otb yAxis
            const otbYaxisValueCorrespondingToPricePositionPaddingTop =
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              (otbYaxisMaxValue / (that.numberOfCompetitors + 1)) *
              that.pricePositionPaddingTop;
            const otbYaxisPaddingTop =
              otbYaxisMaxValue +
              otbYaxisValueCorrespondingToPricePositionPaddingTop;

            tickPositions.push(otbYaxisPaddingTop);

            return tickPositions;
          },
          labels: {
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              const otbYaxisMinMaxValues = that.calculateOtbYaxisMinMaxValue();
              const otbYaxisMaxTickIntervalRange =
                that.calculateOtbYaxisIntervalRange(otbYaxisMinMaxValues.max) /
                100;

              // Hide the paddingTop xAxis label i.e (max + padding) value
              // @ts-expect-error: Incorrect type
              if (this.value > otbYaxisMaxTickIntervalRange) {
                return '';
              }

              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              return that.intl.formatNumber(this.value, { style: 'percent' });
            },
          },
        };

        axisArray.push(primaryYaxis);
      }

      if (this.canShowPricePositionData) {
        const secondaryYaxis = {
          title: {
            text: that.intl.t('overview_dashboard.price_position'),
          },
          reversed: true,
          opposite: axisArray.length !== 0, // only put price position axis opposite (on the right) if other axis will be shown
          gridLineWidth: 0,
          // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
          tickPositioner() {
            const startYaxisValue = 1;
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/restrict-plus-operands
            let endYaxisValue = that.numberOfCompetitors + 1;
            // @ts-expect-error: Incorrect type
            let alternatePositions = [];

            const otbYaxisMinMaxValues = that.calculateOtbYaxisMinMaxValue();

            // alternate positions between startYaxisValue and endYaxisValue i.e. [1, 3, 5, ...endYaxisValue]
            if (endYaxisValue) {
              alternatePositions = Array(endYaxisValue)
                // @ts-expect-error: Incorrect type
                .fill()
                .map((_, index) => startYaxisValue + index)
                .filter((_, index) => index % 2 === 0);
            }

            // In case the OTB yAxis has negative values
            if (
              (that.canShowOtbData || that.canShowShortTermRentalData) &&
              otbYaxisMinMaxValues.min < 0
            ) {
              // The minimum of the interval starts at the nearest OtbYaxis increment from otbYaxisMinMaxValues.min
              const otbYaxisMinTickIntervalRange =
                that.calculateOtbYaxisIntervalRange(
                  Math.abs(otbYaxisMinMaxValues.min),
                );
              // The maximum of the interval ends at the nearest OtbYaxis increment from otbYaxisMinMaxValues.max
              const otbYaxisMaxTickIntervalRange =
                that.calculateOtbYaxisIntervalRange(otbYaxisMinMaxValues.max);

              // max percentage of otb yAxis (otbYaxisMaxTickIntervalRange) corresponds to numberOfCompetitors units of secondary yAxis
              // therefore, 1 percent corresponds to (numberOfCompetitors) / (otbYaxisMaxTickIntervalRange) units of secondary yAxis
              // otbYaxisMinTickIntervalRange of otb yAxis tick correspond to (numberOfCompetitors * otbYaxisMinTickIntervalRange) / (otbYaxisMinMaxValues) units of secondary yAxis
              endYaxisValue +=
                // @ts-expect-error: Incorrect type
                (that.numberOfCompetitors * otbYaxisMinTickIntervalRange) /
                otbYaxisMaxTickIntervalRange;
            }

            // positions
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return [
              that.pricePositionPaddingTop,
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              ...alternatePositions,
              endYaxisValue,
            ];
          },
          labels: {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            formatter() {
              // We hide the value label for endYaxisValue of secondary axis such that only startYxisValue and (numberOfCompetitors + 1) value labels are visible
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
              return this.value > that.numberOfCompetitors + 1 || this.value < 1
                ? ''
                : // @ts-expect-error: Incorrect type
                  `${this.value}`;
            },
          },
        };

        axisArray.push(secondaryYaxis);
      }

      return axisArray;
    }

    return null;
  }

  // The OTB Y-axis increments at the intervals of 25%
  // Returns the interval range in which the given value will fall
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculateOtbYaxisIntervalRange(value) {
    const intervalRange = 25;

    return Math.ceil((value * 100) / intervalRange) * intervalRange;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  calculateOtbYaxisMinMaxValue() {
    const otbYaxisMinMaxValues = {
      min: 0,
      max: 0,
    };

    if (!this.args.isLoading) {
      let baseYAxisValues = [];
      if (this.canShowOtbData) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        baseYAxisValues = this.args.otbData?.fromDates;
      } else if (this.canShowShortTermRentalData) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        baseYAxisValues = this.args.rentalData?.fromDates;
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const yAxisValues = baseYAxisValues
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .map((datapointObject) => {
          const values = [];

          // Include my otb / market otb values in case either of the otb series are visible or none of the pickup series is visible, for instance, only price position visible
          if (
            this.isMyOtbSeriesVisible ||
            this.isMarketOtbSeriesVisible ||
            (!this.isMarketPickupSeriesVisible && !this.isMyPickupSeriesVisible)
          ) {
            values.push(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              datapointObject.getDataValueFromKey(
                this.odSelections,
                OD_API_DATA_KEYS.my_otb_percentage,
              ),
            );
            values.push(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              datapointObject.getDataValueFromKey(
                this.odSelections,
                OD_API_DATA_KEYS.market_otb_percentage,
              ),
            );
          }

          // Consider data values of all possible selectedRefDaysAgo in order to ensure that the graph doesn't jump when we change selectedRefDaysAgo
          Object.values(OD_REF_DAYS_AGO_VALUES).forEach((daysAgoValue) => {
            values.push(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              datapointObject.getDataValueFromKey(
                this.odSelections,
                OD_API_DATA_KEYS.my_otb_percentage_pickup,
                daysAgoValue,
              ),
            );
            values.push(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              datapointObject.getDataValueFromKey(
                this.odSelections,
                OD_API_DATA_KEYS.market_otb_percentage_pickup,
                daysAgoValue,
              ),
            );
          });

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return values;
        })
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        .flat();

      // yAxisValues returned undefined in case no OTB values in the graph
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      otbYaxisMinMaxValues.max = yAxisValues ? Math.max(...yAxisValues) : 1;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      otbYaxisMinMaxValues.min = yAxisValues ? Math.min(...yAxisValues) : 0;
    }

    return otbYaxisMinMaxValues;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  otbYAxisTickPositioner(tickInterval, otbYaxisMinMaxValues) {
    // The minimum of the interval starts at the nearest OtbYaxis increment from otbYaxisMinMaxValues.min
    const otbYaxisMinTickIntervalRange =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      Math.sign(otbYaxisMinMaxValues.min) *
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
      (this.calculateOtbYaxisIntervalRange(Math.abs(otbYaxisMinMaxValues.min)) /
        100);

    // The maximum of the interval ends at the nearest OtbYaxis increment from otbYaxisMinMaxValues.max
    const otbYaxisMaxTickIntervalRange =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.calculateOtbYaxisIntervalRange(otbYaxisMinMaxValues.max) / 100;

    const tickPositions = [];
    for (
      let i = otbYaxisMinTickIntervalRange;
      i <= otbYaxisMaxTickIntervalRange;
      i += tickInterval
    ) {
      tickPositions.push(i);
    }

    return tickPositions;
  }

  @action
  // @ts-expect-error: Incorrect type
  toggleSeries(seriesProperty): void {
    if (
      seriesProperty === 'isMyPriceOpportunitySeriesVisible' &&
      !this.areOpportunitiesInSelectedRange
    ) {
      return;
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this[seriesProperty] = !this[seriesProperty];
  }

  <template>
    {{! @glint-nocheck }}
    <div class="od-performance-rates-chart-container-wrapper">
      {{#if @isLoading}}
        <PrismLoadingBar />
      {{else}}
        <GeneralEmptyStateOtbBanner
          @bannerPosition="top"
          @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.OD_PERFORMANCE_RATES_CHART}}
          @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.OVERVIEW_DASHBOARD}}
        />
        <GeneralChartsOtaChart
          @chartOptions={{this.chartOptions}}
          @model={{this.chartData}}
          @onUpdateChartReference={{@onUpdateChartReference}}
        />

        <div class="od-performance-legend flex items-center justify-center">
          {{#if this.canShowOtbData}}
            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.my_otb)}}
              <GeneralChartsLegendsLegendItem
                @id="my-otb"
                @isHidden={{not this.isMyOtbSeriesVisible}}
                @legendItemClicked={{fn
                  this.toggleSeries
                  "isMyOtbSeriesVisible"
                }}
                @type="dot"
              >
                {{t "rates.my_OTB"}}
              </GeneralChartsLegendsLegendItem>

              <GeneralChartsLegendsLegendItem
                @id="my-pick-up"
                @isHidden={{not this.isMyPickupSeriesVisible}}
                @legendItemClicked={{fn
                  this.toggleSeries
                  "isMyPickupSeriesVisible"
                }}
                @type="dot"
              >
                {{t "overview_dashboard.my_pickup"}}
                {{@selectedRefDaysAgo.label}}
              </GeneralChartsLegendsLegendItem>
            {{/if}}

            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.market_otb)}}
              <GeneralChartsLegendsLegendItem
                @id="market-otb"
                @isHidden={{not this.isMarketOtbSeriesVisible}}
                @legendItemClicked={{fn
                  this.toggleSeries
                  "isMarketOtbSeriesVisible"
                }}
                @type="dot"
              >
                {{t "rates.tooltip.market_otb"}}
              </GeneralChartsLegendsLegendItem>

              <GeneralChartsLegendsLegendItem
                @id="market-pick-up"
                @isHidden={{not this.isMarketPickupSeriesVisible}}
                @legendItemClicked={{fn
                  this.toggleSeries
                  "isMarketPickupSeriesVisible"
                }}
                @type="dot"
              >
                {{t "overview_dashboard.market_pickup"}}
                {{@selectedRefDaysAgo.label}}
              </GeneralChartsLegendsLegendItem>
            {{/if}}
          {{/if}}

          {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.short_term_rental)}}
            <GeneralChartsLegendsLegendItem
              @id="short-term-rental-otb"
              @isHidden={{not this.isRentalOtbSeriesVisible}}
              @legendItemClicked={{fn
                this.toggleSeries
                "isRentalOtbSeriesVisible"
              }}
              @type="dot"
            >
              {{t "alternative_lodging.otb"}}
            </GeneralChartsLegendsLegendItem>
            <GeneralChartsLegendsLegendItem
              @id="short-term-rental-pickup"
              @isHidden={{not this.isRentalPickupSeriesVisible}}
              @legendItemClicked={{fn
                this.toggleSeries
                "isRentalPickupSeriesVisible"
              }}
              @type="dot"
            >
              {{t "short_term_rental.pickup"}}
              {{@selectedRefDaysAgo.label}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}

          {{#if this.canShowPricePositionData}}
            <GeneralChartsLegendsLegendItem
              @id="price-position"
              @isHidden={{not this.isMyPricePositionSeriesVisible}}
              @legendItemClicked={{fn
                this.toggleSeries
                "isMyPricePositionSeriesVisible"
              }}
              @type="line"
            >
              {{t "overview_dashboard.price_position"}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}

          {{#if this.canShowPriceOpportunityData}}
            <GeneralChartsLegendsLegendItem
              @id="price-opportunity"
              @isHidden={{not
                (and
                  this.isMyPriceOpportunitySeriesVisible
                  this.areOpportunitiesInSelectedRange
                )
              }}
              @legendItemClicked={{fn
                this.toggleSeries
                "isMyPriceOpportunitySeriesVisible"
              }}
              @type="dot"
            >
              {{#unless this.areOpportunitiesInSelectedRange}}
                <PrismTooltip
                  @text={{t
                    "overview_dashboard.no_opportunities"
                    refDaysAgo=@selectedRefDaysAgo.label
                  }}
                />
              {{/unless}}
              {{t "overview_dashboard.market.price_opportunity"}}
              {{@selectedRefDaysAgo.label}}
            </GeneralChartsLegendsLegendItem>
          {{/if}}
        </div>

        <div class="hidden" id="dummy-tooltip-for-daily-patterns">
          <HotelOverviewDashboardTooltipsTooltipDailyPatterns
            @isDailyAggregation={{this.isDailyAggregation}}
            @selectedRefDaysAgo={{@selectedRefDaysAgo}}
            @tooltipData={{this.tooltipData}}
          />
        </div>
      {{/if}}
    </div>
  </template>
}
